/**
 * Code-based TanStack Router tree. Express serves this client for every path
 * (config/frontRouter.js), so the router owns the URLs.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { MTM_ROUTE_PATH, PLATFORM } from '@/shared/constants';
import { requireAdmin, requireAuth } from '@/features/auth/require-auth';
import { LoginPage } from '@/features/login/LoginPage';
import { AuthAdPage } from '@/features/auth/AuthAdPage';
import { NotFoundPage } from '@/features/not-found/NotFoundPage';
import { InterfacePage } from '@/features/interface/InterfacePage';
import { InfoPage } from '@/features/info/InfoPage';
import { StoresPage } from '@/features/stores/StoresPage';
import { UsersPage } from '@/features/users/UsersPage';
import { WorkshopsPage } from '@/features/workshops/WorkshopsPage';
import { TranslationsPage } from '@/features/translations/TranslationsPage';
import { ProductLinesPage } from '@/features/product-lines/ProductLinesPage';
import { ProductsPage } from '@/features/products/ProductsPage';
import { StocksPage } from '@/features/stocks/StocksPage';
import { CentralStocksPage } from '@/features/stocks/CentralStocksPage';
import { ModelsPage } from '@/features/mtm/models/ModelsPage';
import { ElementsPage } from '@/features/mtm/elements/ElementsPage';
import { FabricsPage } from '@/features/mtm/fabrics/FabricsPage';
import { EventsPage } from '@/features/mtm/events/EventsPage';
import { FabricBooksPage } from '@/features/mtm/fabric-books/FabricBooksPage';
import { SewingPatternsPage } from '@/features/mtm/sewing-patterns/SewingPatternsPage';
import { SearchAttributesPage } from '@/features/mtm/search-attributes/SearchAttributesPage';
import { LinkPage } from '@/features/mtm/link/LinkPage';
import { PricesPage } from '@/features/mtm/prices/PricesPage';
import { MainPage } from '@/features/orders/MainPage';
import { LabelPage } from '@/features/labels/LabelPage';

export const rootRoute = createRootRoute({
  component: () => (
    <div className="main h-full w-full">
      <Outlet />
      <Toaster position="top-right" richColors closeButton />
    </div>
  ),
  notFoundComponent: NotFoundPage,
});

/** The root goes to the ABCDior orders. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/main' });
  },
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const authAdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ad_auth',
  component: AuthAdPage,
});
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/404',
  component: NotFoundPage,
});

const interfaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/interface',
  beforeLoad: requireAuth,
  component: () => <InterfacePage platform={PLATFORM.ABCDIOR} />,
});

const mtmInterfaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/interface`,
  beforeLoad: requireAuth,
  component: () => <InterfacePage platform={PLATFORM.DMS} />,
});

const infoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/info',
  beforeLoad: requireAuth,
  component: () => <InfoPage platform={PLATFORM.ABCDIOR} />,
});

const mtmInfoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/info`,
  beforeLoad: requireAuth,
  component: () => <InfoPage platform={PLATFORM.DMS} />,
});

const storesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stores',
  beforeLoad: requireAuth,
  component: () => <StoresPage platform={PLATFORM.ABCDIOR} />,
});

const mtmStoresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/stores`,
  beforeLoad: requireAuth,
  component: () => <StoresPage platform={PLATFORM.DMS} />,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  beforeLoad: requireAuth,
  component: () => <UsersPage platform={PLATFORM.ABCDIOR} />,
});

const mtmUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/users`,
  beforeLoad: requireAuth,
  component: () => <UsersPage platform={PLATFORM.DMS} />,
});

const workshopsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workshops',
  beforeLoad: requireAuth,
  component: () => <WorkshopsPage />,
});

const translationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/translations',
  beforeLoad: requireAdmin(PLATFORM.ABCDIOR),
  component: () => <TranslationsPage platform={PLATFORM.ABCDIOR} />,
});

const mtmTranslationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/translations`,
  beforeLoad: requireAdmin(PLATFORM.DMS),
  component: () => <TranslationsPage platform={PLATFORM.DMS} />,
});

const productLinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/product-lines',
  beforeLoad: requireAuth,
  component: () => <ProductLinesPage />,
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  beforeLoad: requireAuth,
  component: () => <ProductsPage />,
});

const stocksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stocks',
  beforeLoad: requireAuth,
  component: () => <StocksPage />,
});

const centralStocksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/central-stocks',
  beforeLoad: requireAuth,
  component: () => <CentralStocksPage />,
});

const mtmModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/models`,
  beforeLoad: requireAuth,
  component: () => <ModelsPage />,
});

const mtmElementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/elements`,
  beforeLoad: requireAuth,
  component: () => <ElementsPage />,
});

const mtmFabricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/fabrics`,
  beforeLoad: requireAuth,
  component: () => <FabricsPage />,
});

const mtmEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/events`,
  beforeLoad: requireAuth,
  component: () => <EventsPage />,
});

const mtmFabricBooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/configuration/fabric-book`,
  beforeLoad: requireAuth,
  component: () => <FabricBooksPage />,
});

const mtmSewingPatternsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/configuration/sewing-pattern`,
  beforeLoad: requireAuth,
  component: () => <SewingPatternsPage />,
});

const mtmSearchAttributesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/configuration/filters`,
  beforeLoad: requireAdmin(PLATFORM.DMS),
  component: SearchAttributesPage,
});

const mtmLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/link`,
  beforeLoad: requireAuth,
  component: () => <LinkPage />,
});

const mtmPricesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/prices`,
  beforeLoad: requireAuth,
  component: () => <PricesPage />,
});

const labelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/label/$custId',
  component: LabelPage,
});

const mainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/main',
  beforeLoad: requireAuth,
  component: () => <MainPage />,
});

const mtmMainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: `/${MTM_ROUTE_PATH}/main`,
  beforeLoad: requireAuth,
  component: () => <MainPage platform={PLATFORM.DMS} />,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authAdRoute,
  notFoundRoute,
  interfaceRoute,
  mtmInterfaceRoute,
  infoRoute,
  mtmInfoRoute,
  storesRoute,
  mtmStoresRoute,
  usersRoute,
  mtmUsersRoute,
  workshopsRoute,
  translationsRoute,
  mtmTranslationsRoute,
  productLinesRoute,
  productsRoute,
  stocksRoute,
  centralStocksRoute,
  mtmModelsRoute,
  mtmElementsRoute,
  mtmFabricsRoute,
  mtmEventsRoute,
  mtmFabricBooksRoute,
  mtmSewingPatternsRoute,
  mtmSearchAttributesRoute,
  mtmLinkRoute,
  mtmPricesRoute,
  mainRoute,
  mtmMainRoute,
  labelRoute,
]);

export const createAppRouter = () =>
  createRouter({
    routeTree,
    defaultPreload: 'intent',
    scrollRestoration: true,
  });

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
